// The browser half of the impersonation contract. The server decides
// (worker/src/security.ts); this makes the SCREEN agree with it, so a partner
// never spends effort on a control whose write the server would refuse, and
// the sentence they read is the sentence the server would have given.
//
// F138e: the preview is no longer read-only in one flat sense. It has two
// modes - "jen koukám" and "můžu jednat" - and in the acting mode a bounded
// list of writes goes through (shared/impersonation-writes.ts, the SAME module
// the Worker enforces). Everything else is still refused before it leaves the
// browser, and now refused BY NAME.

import {
  impersonationWriteDecision,
  type ImpersonationWriteRefusal
} from "../shared/impersonation-writes";

export const READONLY_BLOCKED_EVENT = "launchpad:readonly-blocked";

/**
 * The error code the gate answers a blocked write with, and the same code the
 * Worker's own `mutationSecurity` returns for the same refusal. ONE string,
 * two producers, so a surface can tell "the gate stopped this" apart from an
 * ordinary 403 without guessing from the status (F150).
 */
export const READONLY_BLOCKED_ERROR = "impersonation_readonly";

/** The mode the fetch gate is running under. `null` = not impersonating. */
export type ImpersonationGateMode = "look" | "act" | null;

export type GateDecision = "pass" | "block";

/**
 * In `look` mode nothing but session management passes, whatever the allowance
 * list says: the partner has not taken the deliberate step to acting.
 */
export function readonlyGateDecision(
  method: string | undefined,
  url: string,
  mode: ImpersonationGateMode = "look"
): GateDecision {
  return readonlyGateVerdict(method, url, mode).decision;
}

export function readonlyGateVerdict(
  method: string | undefined,
  url: string,
  mode: ImpersonationGateMode = "look"
): { decision: GateDecision; refusal: ImpersonationWriteRefusal | null } {
  let path: string;
  try {
    path = new URL(url, "http://readonly.local").pathname;
  } catch {
    return { decision: "block", refusal: "other" };
  }
  // Only our own API is gated: a request to a third party (an artifact, a
  // Drive file, an analytics beacon) is not a write to this client's record,
  // whatever its method. 🔴 An earlier draft gated non-API writes too and the
  // pre-existing suite caught it.
  if (!path.startsWith("/api/")) return { decision: "pass", refusal: null };
  const verb = (method ?? "GET").toUpperCase();
  const answer = impersonationWriteDecision(verb, path);
  if (!answer.allow) return { decision: "block", refusal: answer.refusal };
  if (answer.allowance === "session") return { decision: "pass", refusal: null };
  // An allowed write still needs the acting mode. `look` refuses it with its
  // own name, so the bar can offer the switch instead of a dead end.
  return mode === "act"
    ? { decision: "pass", refusal: null }
    : { decision: "block", refusal: "looking" };
}

export function notifyReadonlyBlocked(refusal: ImpersonationWriteRefusal = "looking"): void {
  window.dispatchEvent(new CustomEvent(READONLY_BLOCKED_EVENT, { detail: { refusal } }));
}

/**
 * Act-approve (Pavel 2026-08-19): a surface that read `impersonation_readonly`
 * out of a response hands the refusal to the bar BY NAME instead of printing
 * the generic client `forbidden` sentence ("Tahle akce ti nepatří...") - the
 * doubly wrong answer Pavel met on the strategy approve. One refusal has one
 * home (the F150 law), and the bar is the surface that can name it: the SAME
 * shared module the Worker enforces answers which refusal this path is under
 * the current mode. When the local gate would have PASSED the write the
 * modules disagree (a stale bundle against a newer Worker), and "other" is the
 * honest name for a refusal we cannot explain.
 */
export function reportBlockedWrite(method: string | undefined, url: string): void {
  const verdict = readonlyGateVerdict(method, url, activeMode);
  notifyReadonlyBlocked(verdict.refusal ?? "other");
}

let restoreFetch: (() => void) | null = null;
let activeMode: ImpersonationGateMode = null;

export function readonlyModeActive(): boolean {
  return restoreFetch !== null;
}

/**
 * The question the voice entry points ask before they spend a recording on a
 * write that cannot land. In the acting mode a dictated note IS allowed, so
 * refusing there would take away the capability F138e came to add.
 */
export function readonlyBlocksVoice(): boolean {
  return activeMode === "look";
}

/** The mode the gate is currently enforcing, for the surfaces that ask. */
export function readonlyGateMode(): ImpersonationGateMode {
  return activeMode;
}

export function setReadonlyMode(mode: ImpersonationGateMode): void {
  activeMode = mode;
  const shouldGate = mode !== null;
  if (shouldGate === readonlyModeActive()) return;
  if (!shouldGate) {
    restoreFetch?.();
    restoreFetch = null;
    return;
  }
  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method ?? (input instanceof Request ? input.method : undefined);
    const verdict = readonlyGateVerdict(method, url, activeMode);
    if (verdict.decision === "block") {
      notifyReadonlyBlocked(verdict.refusal ?? "other");
      return Promise.resolve(
        new Response(JSON.stringify({ error: READONLY_BLOCKED_ERROR }), {
          status: 403,
          headers: { "Content-Type": "application/json" }
        })
      );
    }
    return original(input, init);
  };
  restoreFetch = () => {
    window.fetch = original;
  };
}
